/**
 * The convoy's own radios, so they do not read as somebody following it.
 *
 * Capture is a short scan the operator reviews. Fleet kit from the registry
 * is already ours; everything else waits for a tick in that list.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShieldIcon from '@mui/icons-material/GppGoodOutlined';
import {
  MissionBaseline,
  getMissionBaseline,
  untrustIdentifier,
} from '../../services/missionApi';
import OwnKitReview from './OwnKitReview';
import { formatWhen } from './format';

const GOLD = '#f5b942';

const SOURCE_LABEL: Record<string, string> = {
  sweep: 'Learned at departure',
  manual: 'Trusted by hand',
  registry: 'Fleet kit',
};

interface Props {
  missionId: number;
  /** Live runs get the capture controls; a plan only gets the summary. */
  inProgress: boolean;
  onError: (message: string) => void;
  /** Bump when kit was captured elsewhere (setup wizard, Follows). */
  refreshKey?: number;
}

const MissionBaselinePanel: React.FC<Props> = ({ missionId, onError, refreshKey }) => {
  const [baseline, setBaseline] = useState<MissionBaseline | null>(null);
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setBaseline(await getMissionBaseline(missionId));
    } catch {
      setBaseline(null);
    }
  }, [missionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const untrust = async (identifier: string) => {
    setBusy(true);
    try {
      setBaseline(await untrustIdentifier(missionId, identifier));
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Could not remove that radio');
    } finally {
      setBusy(false);
    }
  };

  const entries = baseline?.entries ?? [];
  const state = baseline?.state ?? 'none';
  const summary =
    state === 'capturing' ? 'Scan and pick own kit'
      : entries.length
        ? `${entries.length} own ${entries.length === 1 ? 'emitter' : 'emitters'}`
        : 'No baseline yet';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
        <ShieldIcon sx={{ fontSize: 15, color: entries.length ? GOLD : 'text.disabled' }} />
        <Typography variant="caption" sx={{ fontWeight: 700 }}>Own kit</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {summary}
        </Typography>
        <Button
          size="small"
          onClick={() => { setReview(true); setOpen(true); }}
          sx={{
            fontSize: '0.68rem',
            py: 0,
            minHeight: 26,
            px: 1,
            color: '#c6cdd5',
            border: '1px solid #333a45',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          Capture
        </Button>
        <Button
          size="small"
          onClick={() => { setReview(false); setOpen(true); }}
          sx={{
            fontSize: '0.68rem',
            py: 0,
            minHeight: 26,
            px: 1,
            color: '#c6cdd5',
            border: '1px solid #333a45',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          Manage
        </Button>
      </Box>

      {baseline?.reason && (
        <Typography variant="caption" sx={{ display: 'block', color: '#ef9a9a' }}>
          {baseline.reason}
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
              Own kit on this mission
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            Start a 60s scan so every assigned device can check in. Anything
            heard in that minute or the minute before can be marked ours.
          </Typography>

          {review && (
            <Box sx={{ mb: 1.25 }}>
              <OwnKitReview
                missionId={missionId}
                onError={onError}
                onChanged={load}
              />
            </Box>
          )}

          {entries.length === 0 ? (
            !review && (
              <Typography variant="caption" color="text.secondary">
                Nothing trusted yet. Capture to review a short scan.
              </Typography>
            )
          ) : (
            <Box sx={{ display: 'grid', gap: 0.25, maxHeight: 220, overflowY: 'auto' }}>
              {entries.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {entry.identifier}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                    {entry.name || entry.signal_type}
                  </Typography>
                  <Tooltip title={`${SOURCE_LABEL[entry.source] || entry.source} · ${formatWhen(entry.captured_at)}`}>
                    <Chip
                      label={entry.source}
                      size="small"
                      sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }}
                    />
                  </Tooltip>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => untrust(entry.identifier)}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {!review && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Typography
                variant="caption"
                role="button"
                tabIndex={0}
                onClick={() => setReview(true)}
                sx={{ fontWeight: 700, cursor: 'pointer', color: GOLD }}
              >
                Scan and pick more
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MissionBaselinePanel;
